import { setTokens } from '@/services/apiClient'
import { logout as logoutRequest } from '@/services/loginApi'
import { fetchUser } from '@/services/accountApi'

export const settings = {
  namespaced: true,
  state: {
    useMockApi: false,
    persistConversations: true,
    userName: "",
    accessToken: "",
    refreshToken: "",
    isDemo: false,
    isStaff: false,
    isSuperuser: false,
    organisationBanner: null,
    systemPrompt: `Tu es un assistant expert dans la réalisation d’ouvrages en plaques de plâtre et en isolation. Tu t’appuies sur les DTU applicables (notamment DTU 25.41) et sur les recommandations fabricants pour garantir la conformité technique. Tu disposes : d’un guide de formation général (explications pédagogiques), d’une base produits plaques (Pregyplac BA13 standard, hydro H1, haute dureté Pregyroc, acoustique Pregyplac DB, multi‑performance, BA6 pour courbes) et d’une base isolants (laine de verre GR32, isolant phonique LV pour cloisons, Rockmur pour murs, fibre de bois biosourcée), ainsi que de systèmes de référence (dont Par Phonic) avec présentations et quantitatifs.

Avant toute recommandation, tu interroges toujours l’utilisateur pour définir précisément le projet (type d’ouvrage, dimensions, exigences feu/acoustique/humidité, usage du local, contraintes mécaniques, etc.). Tu structures ensuite ta réponse en trois étapes :
1️⃣ **Définition du projet** (contexte et hypothèses),
2️⃣ **Proposition technique + quantitatif synthétique** (avec tableau clair et unités),
3️⃣ **Ouverture technique** (détails de pose, variantes, fiches produits, etc.).

Tu utilises les **références commerciales réelles** des produits disponibles en France. Si une référence commerciale exacte n’existe pas ou n’est pas connue, tu emploies un **nom générique explicite** (ex. *bande mousse PE*, *mastic coupe-feu certifié*, *enduit à joint poudre*), et tu **indiques clairement à l’utilisateur** qu’il s’agit d’une désignation générique à adapter selon le fabricant choisi.

Tu n’inventes jamais de noms de produits. Tu restes rigoureux, clair et professionnel, en t’appuyant sur les DTU et les PV feu/acoustiques reconnus. En cas d’incertitude, tu la signales et proposes l’option la plus sûre.
`,
    overrideSystemPrompt: false,
    darkMode: false,
    showMaterialDetails: false,
    showProductImages: false,
  },
  mutations: {
    SET_SETTINGS(state, settings) {
      Object.assign(state, settings)
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_USE_MOCK_API(state, value) {
      state.useMockApi = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_PERSIST_CONVERSATIONS(state, value) {
      state.persistConversations = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_SYSTEM_PROMPT(state, value) {
      state.systemPrompt = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_DARK_MODE(state, value) {
      state.darkMode = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_SHOW_MATERIAL_DETAILS(state, value) {
      state.showMaterialDetails = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_SHOW_PRODUCT_IMAGES(state, value) {
      state.showProductImages = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_OVERRIDE_SYSTEM_PROMPT(state, value) {
      state.overrideSystemPrompt = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_USER_NAME(state, value) {
      state.userName = value
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_USER(state, { username, access, refresh, is_demo, is_staff, is_superuser, organisation_banner }) {
      state.userName = username
      state.accessToken = access || state.accessToken
      state.refreshToken = refresh || state.refreshToken
      state.isDemo = is_demo
      state.isStaff = is_staff
      state.isSuperuser = is_superuser || false
      state.organisationBanner = organisation_banner || null
      setTokens(state.accessToken, state.refreshToken)
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    SET_TOKENS(state, { access, refresh }) {
      state.accessToken = access || state.accessToken
      state.refreshToken = refresh || state.refreshToken
      setTokens(state.accessToken, state.refreshToken)
      localStorage.setItem("optinego_appSettings", JSON.stringify(state))
    },
    LOGOUT(state) {
      state.userName = ''
      state.accessToken = ''
      state.refreshToken = ''
      state.isDemo = false
      state.isStaff = false
      state.isSuperuser = false
      state.organisationBanner = null
      setTokens('', '')
      localStorage.removeItem('optinego_appSettings')
    }
  },
  actions: {
    initSettings({ commit }) {
      const savedSettings = localStorage.getItem("optinego_appSettings")
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings)
          commit("SET_SETTINGS", parsed)
          if (parsed.accessToken) setTokens(parsed.accessToken, parsed.refreshToken)
        } catch (e) {
          console.warn("Failed to parse saved settings, using defaults")
        }
      }
    },

    toggleMockApi({ commit }, value) {
      commit("SET_USE_MOCK_API", value)
    },

    togglePersistConversations({ commit }, value) {
      commit("SET_PERSIST_CONVERSATIONS", value)
    },

    updateSystemPrompt({ commit }, value) {
      commit("SET_SYSTEM_PROMPT", value)
    },

    toggleDarkMode({ commit }, value) {
      commit("SET_DARK_MODE", value)
    },
    toggleMaterialDetails({ commit }, value) {
      commit("SET_SHOW_MATERIAL_DETAILS", value)
    },
    toggleProductImages({ commit }, value) {
      commit("SET_SHOW_PRODUCT_IMAGES", value)
    },
    toggleOverrideSystemPrompt({ commit }, value) {
      commit("SET_OVERRIDE_SYSTEM_PROMPT", value)
    },

    updateUserName({ commit }, value) {
      commit("SET_USER_NAME", value)
    },
    setUser({ commit }, user) {
      commit("SET_USER", user)
    },
    async refreshProfile({ commit }) {
      const profile = await fetchUser()
      commit("SET_USER", profile)
    },
    updateTokens({ commit }, { access, refresh }) {
      commit("SET_TOKENS", { access, refresh })
    },
    async logout({ commit, state }) {
      const refreshToken = state.refreshToken
      // Clear local state first so the router guard sees a logged-out state
      // immediately — the server-side blacklist call is best-effort and must
      // not delay it (a redirect issued while this is in flight would
      // otherwise still see a stale, truthy accessToken).
      commit("LOGOUT")
      if (refreshToken) {
        logoutRequest(refreshToken).catch(() => {})
      }
    }
  },
  getters: {
    useMockApi: (state) => state.useMockApi,
    persistConversations: (state) => state.persistConversations,
    userName: (state) => state.userName,
    accessToken: (state) => state.accessToken,
    isDemo: (state) => state.isDemo,
    isStaff: (state) => state.isStaff,
    isSuperuser: (state) => state.isSuperuser,
    systemPrompt: (state) => state.systemPrompt,
    overrideSystemPrompt: (state) => state.overrideSystemPrompt,
    darkMode: (state) => state.darkMode,
    showMaterialDetails: (state) => state.showMaterialDetails,
    showProductImages: (state) => state.showProductImages,
    organisationBanner: (state) => state.organisationBanner,
  }
}